import classNames from 'classnames';
import classnames from 'classnames';
import React, { useContext, useEffect, useRef, useState } from 'react';
import EmptyKpiPlaceholder from '../../components/EmptyKpiPlaceholder/EmptyKpiPlaceholder';
import BarChart from '../../components/NivoCharts/BarChart/BarChart';
import LineChart from '../../components/NivoCharts/LineChart/LineChart';
import PieChart from '../../components/NivoCharts/PieChart/PieChart';
import queryString from 'query-string';
import quickvueVirtualLogo from '../../assets/images/JMIQuickVUEVirtualLogo.png';

// SERVICES
import { getClientConfiguration, getClientInvisionIQKpis, getClientList, getClientListSelectVM } from '../../services';
import { Autocomplete } from '../../components/Autocomplete';
import { AppContext } from '../../AppContext';
import roles from '../../helpers/roles';
import KpiFilterPanel from '../../components/KpiFilterPanel/KpiFilterPanel';
import { cloneDeep } from 'lodash';
import { useRouteMatch, withRouter } from 'react-router';
import { withStyles } from '@material-ui/core';
import Loading from '../../components/Loading';

// Bringing in app version from Package.json
var { version: appVersion } = require('../../../package.json');

const CHART_TYPES = {
  BAR: 'Bar',
  PIE: 'Pie',
  LINE: 'Line',
  TEXT: 'Text',
};

const CHART_NAMES = {
  TOTAL_ORDERS: 'Total Orders',
  DATA_HIT: 'Data Hit',
  IMAGERY_AVAILABLE: 'Imagery Available',
  HIGH_PRIORITY: 'High Priority',
  SUCCESSFULLY_CALCULATED_ITV: 'Successfully Calculated ITV',
  ITV_UNDERINSURED: 'ITV % - Underinsured',
  ITV_OVERINSURED: 'ITV % - Overinsured',
  ROOF_ISSUES: 'Roof Issues',
  CONDITIONS_EXPOSURES: 'Conditions / Exposures',
  FIRE_PROTECTION: 'Fire Protection',
  RENOVATIONS: 'Renovations',
  WEATHER_RISK: 'Weather Risk',

  DEFENSIBLE_SPACE_ZONE_1: 'Defensible Space Zone 1',
  DEFENSIBLE_SPACE_ZONE_2: 'Defensible Space Zone 2',
  DEFENSIBLE_SPACE_ZONE_3: 'Defensible Space Zone 3',
  // DEFENSIBLE_SPACE: 'Defensible Space',
};

const chartCompMap = {
  [CHART_TYPES.BAR]: BarChart,
  [CHART_TYPES.PIE]: PieChart,
  [CHART_TYPES.LINE]: LineChart,
};

const styles = theme => {
  return {
    main: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
    },

    filterPanel: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      gridGap: '1rem',
      textAlign: 'left',
    },

    filterPanelClientSelect: {
      gridColumn: 'span 6',
    },
    filterPanelHalfGap: {
      gridColumn: 'span 6',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',

      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },

    header: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '2rem',
    },
    headerLeft: {
      //
    },
    headerCenter: {
      flex: 1,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
    },
    headerRight: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },
    banner: {
      width: '100%',
      maxWidth: 'calc(100vw / 3)',
      height: 'auto',
      borderRadius: 10,
      objectFit: 'cover',
    },
    bannerContainer: {
      width: 200,
      borderRadius: 10,
      overflow: 'hidden',
    },
    bannerContainerEmpty: {
      border: '2px dashed #ccc',
      height: 100,
      marginBottom: 25,
      padding: '0 20px',
    },
    bannerEmptyState: {
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#AAA',
      fontSize: '1.5rem',
    },
    headerTitle: {
      margin: 0,
    },
    headerSubtitle: {
      margin: 0,
      color: 'var(--jmi-green)',
    },

    divider: {
      width: '100%',
      height: 2,
      backgroundColor: '#ddd',
      margin: '20px 0',
    },

    waitingOnClientMessage: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-s tart',
      alignItems: 'flex-start',
      marginBottom: 30,
    },

    textKpisContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
    textKpiContainer: {
      margin: '0 2rem',
    },

    chartKpisContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(12, 1fr)',
      gridAutoRows: '1fr',
      gridAutoFlow: 'dense',
      width: '100%',
      userSelect: 'none',
      marginBottom: 20,
    },
    chartKpiContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minWidth: 0, // allow child flex items to shrink down when window is shrunk

      gridColumn: 'span 4',
    },

    chartKpiContainerSpan3: {
      gridColumn: 'span 3',
    },
    chartKpiContainerSpan4: {
      gridColumn: 'span 4',
    },

    borderTopDivider: {
      borderTop: '2px solid #ddd',
      padding: '1rem 0',
    },
  };
};

const GRID_DEFAULT_SORTING_FIELD_BY_KPI = {
  [CHART_NAMES.DATA_HIT]: {
    field: 'visualScore',
    direction: 'asc',
  },
  [CHART_NAMES.ITV_UNDERINSURED]: {
    field: 'itv',
    direction: 'asc',
  },
  [CHART_NAMES.ITV_OVERINSURED]: {
    field: 'itv',
    direction: 'desc',
  },

  [CHART_NAMES.DEFENSIBLE_SPACE_ZONE_1]: {
    field: 'defensiblePercentZone1',
    direction: 'asc',
  },
  [CHART_NAMES.DEFENSIBLE_SPACE_ZONE_2]: {
    field: 'defensiblePercentZone2',
    direction: 'asc',
  },
  [CHART_NAMES.DEFENSIBLE_SPACE_ZONE_3]: {
    field: 'defensiblePercentZone3',
    direction: 'asc',
  },
  // [CHART_NAMES.DEFENSIBLE_SPACE]: {
  //   field: 'defensibleSpace',
  //   direction: 'asc',
  // },
};

const InVisionIQ = withRouter(
  withStyles(styles)(({ classes, history, match, location }) => {
    // #region HOOKS
    const {
      state: { roles: userRoles },
    } = useContext(AppContext);
    // #endregion

    // #region STATE
    const [clientList, setClientList] = useState([]);
    const [clientListLoading, setClientListLoading] = useState(false);

    const [selectedClientOpt, setSelectedClientOpt] = useState(null);
    const [client, setClient] = useState(null);

    const [kpisLoading, setKpisLoading] = useState(false);
    const [kpiError, setKpiError] = useState(null);
    const [textKpis, setTextKpis] = useState(null);
    const [chartKpis, setChartKpis] = useState(null);
    // #endregion

    // #region REFS
    const queryParamsRef = useRef({});
    // #endregion

    // #region HOOKS
    //
    // #emdregion

    // #region COMPUTED
    const showClientPicker = userRoles.includes(roles.ADMIN);
    // #endregion

    // #region FUNCTIONS
    const getChart = kpi => {
      const Chart = chartCompMap[kpi.type];

      if (!Chart || !kpi.data?.length) {
        const text = !Chart
          ? 'Chart type not supported'
          : 'There is no data for this KPI based on the applied filters. Try changing the filters above to retrieve more data.';
        return (
          <EmptyKpiPlaceholder
            text={text}
            style={{
              color: '#777',
              border: '2px dashed #ccc',
              padding: '15px',
              borderRadius: 10,
              marginTop: '2rem',
            }}
          />
        );
      }

      const extraChartProps = {
        onClick: node => {
          // don't open link if there are 0 orders
          if (!node.value) {
            return;
          }

          const sort = GRID_DEFAULT_SORTING_FIELD_BY_KPI[kpi.label];

          const query = queryString.stringify({
            ...queryParamsRef.current,
            [kpi.filterColumn]: node.id,
            sortField: sort?.field,
            sortDirection: sort?.direction,
          });

          const url = window.location.href + `/${kpi.sortOrder}?${query}`;

          window.open(url, '_blank');
        },
      };

      const data = cloneDeep(kpi.data).reverse();

      return <Chart {...kpi} data={data} reverse {...extraChartProps} />;
    };

    const renderKpiLabel = kpi => {
      return (
        <h3
          style={{
            margin: 0,
            color: '#777',
          }}
        >
          {kpi.label}
        </h3>
      );
    };

    const renderTextKpi = kpi => {
      const text = kpi.primaryMetricLabel;

      return (
        <div key={kpi.label} className={classnames(classes.textKpiContainer)}>
          {renderKpiLabel(kpi)}

          <h2 style={{ margin: 0 }}>{text}</h2>
        </div>
      );
    };

    const renderChartKpi = kpi => {
      const chart = getChart(kpi);

      const spanClass = [
        CHART_NAMES.WEATHER_RISK,
        CHART_NAMES.CONDITIONS_EXPOSURES,
        CHART_NAMES.FIRE_PROTECTION,
        CHART_NAMES.RENOVATIONS,
      ].includes(kpi.label)
        ? classes.chartKpiContainerSpan3
        : classes.chartKpiContainerSpan4;

      return (
        <div
          key={kpi.label}
          className={classnames(
            //
            classes.borderTopDivider,
            classes.chartKpiContainer,
            spanClass,
          )}
        >
          {renderKpiLabel(kpi)}

          {chart}
        </div>
      );
    };

    const getKpis = async filterParams => {
      try {
        console.log('getKpis', filterParams);
        console.log('showClientPicker', showClientPicker);
        console.log('selectedClientOpt', selectedClientOpt);
        console.log('selectedClientOpt?.value', selectedClientOpt?.value);
        if (showClientPicker && !selectedClientOpt?.value) return;

        setKpisLoading(true);

        queryParamsRef.current = {
          clientId: selectedClientOpt?.value,
          ...filterParams,
        };

        let { kpis, clientLogo, clientName } = await getClientInvisionIQKpis(queryParamsRef.current);
        //quick fix to get the client based on the client name
        //replace this with the client id from the KPI response
        let matchingClient = null;
        // For non-admin users, set the client based on the KPI response
        if (!showClientPicker && clientName && clientList.length > 0) {
          matchingClient = clientList.find(c => c.label.startsWith(clientName));
          if (matchingClient) {
            console.log('matchingClient', matchingClient);
            setSelectedClientOpt(matchingClient);
          }
        }
        
        let clientConfiguration = await getClientConfiguration(selectedClientOpt?.value ?? matchingClient?.value);

        const textBased = kpis.filter(kpi => kpi.type === CHART_TYPES.TEXT);
        let chartBased = kpis.filter(kpi => kpi.type !== CHART_TYPES.TEXT);

        if (clientConfiguration !== null && clientConfiguration !== undefined && clientConfiguration.virtualValuationProviderId === 2) {
          const itvLabels = [CHART_NAMES.ITV_UNDERINSURED, CHART_NAMES.ITV_OVERINSURED, CHART_NAMES.SUCCESSFULLY_CALCULATED_ITV];
          chartBased = chartBased.filter(item => {
            return !itvLabels.includes(item?.label);
          });
        }

        setTextKpis(textBased);
        setChartKpis(chartBased);

        setClient({
          name: clientName,
          logo: clientLogo,
        });
      } catch (error) {
        setKpiError('No data available');
        setTextKpis([]);
        setChartKpis([]);
      } finally {
        setKpisLoading(false);
      }
    };

    const handleKpiRefresh = filterParams => {
      getKpis(filterParams);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
      const getClientListAsync = async () => {
        console.log('getClientListAsync');

        try {
          setClientListLoading(true);
          const clients = await getClientListSelectVM();
          setClientList(
            clients.map(client => ({
              value: client.clientId,
              label: `${client.clientName} ${client.clientCode && `(${client.clientCode})`}`,
            })),
          );
        } catch (error) {
          //
        }
        setClientListLoading(false);
      };

      // Load client list for both admin and non-admin users
      getClientListAsync();
    }, []);
    // #endregion

    return (
      <div className={classes.main}>
        {
          <div className={classes.filterPanel}>
            {showClientPicker ? (
              <>
                <div className={classes.filterPanelClientSelect}>
                  <Autocomplete
                    value={selectedClientOpt}
                    loading={clientListLoading || kpisLoading}
                    placeholder="Please select a client"
                    options={clientList}
                    onChange={opt => setSelectedClientOpt(opt)}
                  />
                </div>

                <div className={classes.filterPanelHalfGap}>{/* <input type="color" /> */}</div>
              </>
            ) : null}

            {(!showClientPicker || selectedClientOpt?.value) && (
              <KpiFilterPanel
                //
                loading={kpisLoading}
                onRefresh={handleKpiRefresh}
                clientId={selectedClientOpt?.value}
              />
            )}
          </div>
        }

        <div className="flex-1 flex-column">
          {(!showClientPicker || !!selectedClientOpt?.value) && (!!chartKpis?.length || !!textKpis?.length) ? (
            <>
              <div className={classnames(showClientPicker && classes.borderTopDivider, classes.header)}>
                <div className={classes.headerLeft}>
                  <div className={classNames(classes.bannerContainer)}>
                    <img className={classes.banner} src={quickvueVirtualLogo} />
                  </div>
                </div>

                <div className={classes.headerCenter}>
                  <div className={classes.textKpisContainer}>{textKpis?.map(renderTextKpi)}</div>
                </div>

                <div className={classes.headerRight}>
                  <div className={classNames(classes.bannerContainer, !client?.logo && classes.bannerContainerEmpty)}>
                    {!!client?.logo ? (
                      <img className={classes.banner} src={client?.logo} />
                    ) : (
                      <div className={classes.bannerEmptyState}>No logo provided</div>
                    )}
                  </div>
                </div>
              </div>

              <div className={classes.chartKpisContainer}>{chartKpis?.map(renderChartKpi)}</div>
            </>
          ) : (
            <div className={classes.waitingOnClientMessage}>
              {!!kpiError ? (
                <h3 style={{ color: 'var(--jmi-red)' }}>{kpiError}</h3>
              ) : (
                <h2 style={{ color: '#777' }}>
                  {showClientPicker
                    ? !!selectedClientOpt?.value
                      ? 'Please choose data filters'
                      : 'Waiting for client selection...'
                    : kpisLoading
                    ? 'Loading results...'
                    : 'Please choose data filters'}
                </h2>
              )}
            </div>
          )}
        </div>

        <div
          className={classnames('row-center-center', 'bg-white')}
          style={{ padding: 10, fontSize: 12, color: '#aaa' }}
        >
          Client Portal - Version {appVersion}
        </div>
      </div>
    );
  }),
);

InVisionIQ.propTypes = {};

InVisionIQ.defaultProps = {};

export default InVisionIQ;
